import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Inbox, FileText, ClipboardList, Users, DollarSign,
  Bell, MessageSquare, LogOut, Headset, ChevronDown, Loader2, Images, Star, Plug, Palette, Newspaper,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/landing/shared";
import AdminLogin from "./AdminLogin";
import DashboardHome from "./DashboardHome";
import LeadsPage from "./LeadsPage";
import CarouselPage from "./CarouselPage";
import ReviewsPage from "./ReviewsPage";
import IntegrationsPage from "./IntegrationsPage";
import ThemePage from "./ThemePage";
import BlogPage from "./BlogPage";
import QuotesPage from "./QuotesPage";
import OrdersPage from "./OrdersPage";
import ClientsPage from "./ClientsPage";
import FinancialPage from "./FinancialPage";

const NAV = [
  { to: "/admindashboard", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admindashboard/leads", icon: Inbox, label: "Leads do Site" },
  { to: "/admindashboard/carrossel", icon: Images, label: "Carrossel" },
  { to: "/admindashboard/depoimentos", icon: Star, label: "Depoimentos" },
  { to: "/admindashboard/integracoes", icon: Plug, label: "Integrações" },
  { to: "/admindashboard/personalizar", icon: Palette, label: "Personalizar" },
  { to: "/admindashboard/blog", icon: Newspaper, label: "Blog" },
  { to: "/admindashboard/orcamentos", icon: FileText, label: "Orçamentos" },
  { to: "/admindashboard/ordens", icon: ClipboardList, label: "Ordens de Serviço" },
  { to: "/admindashboard/clientes", icon: Users, label: "Clientes" },
  { to: "/admindashboard/financeiro", icon: DollarSign, label: "Financeiro" },
];

const TITLES = {
  "/admindashboard": "Dashboard",
  "/admindashboard/leads": "Leads do Site",
  "/admindashboard/carrossel": "Carrossel",
  "/admindashboard/depoimentos": "Depoimentos",
  "/admindashboard/integracoes": "Integrações",
  "/admindashboard/personalizar": "Personalizar Site",
  "/admindashboard/blog": "Blog & Dicas",
  "/admindashboard/orcamentos": "Orçamentos",
  "/admindashboard/ordens": "Ordens de Serviço",
  "/admindashboard/clientes": "Clientes",
  "/admindashboard/financeiro": "Financeiro",
};

export default function AdminApp() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0B0D]" data-testid="admin-loading">
        <Loader2 size={32} className="animate-spin text-[#FFB800]" />
      </div>
    );
  }
  if (!user) return <AdminLogin />;

  const title = TITLES[location.pathname] || "Dashboard";

  return (
    <div className="flex min-h-screen bg-[#0A0C10]" data-testid="admin-dashboard">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[240px] flex-col border-r border-white/5 bg-[#0C0E12] lg:flex" data-testid="admin-sidebar">
        <div className="px-6 pb-6 pt-7">
          <Logo />
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3" data-testid="admin-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 font-display text-sm font-semibold uppercase tracking-wide transition-colors ${
                  isActive ? "bg-[#FFB800] text-black" : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="m-4 rounded-2xl border border-white/5 bg-[#11141A] p-4">
          <div className="flex items-center gap-3">
            <Headset size={22} className="text-zinc-400" />
            <div>
              <p className="text-xs text-zinc-400">Precisa de ajuda?</p>
              <p className="text-xs font-bold text-[#FFB800]">Suporte Online</p>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col lg:ml-[240px]">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-white/5 bg-[#0C0E12]/90 px-5 backdrop-blur lg:px-8" data-testid="admin-header">
          <div className="flex items-center gap-3">
            <span className="font-display text-lg font-bold uppercase tracking-wide text-white">{title}</span>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="admin-bell" className="relative text-zinc-400 transition-colors hover:text-white" aria-label="Notificações">
              <Bell size={19} />
              <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">3</span>
            </button>
            <button data-testid="admin-messages" className="relative text-zinc-400 transition-colors hover:text-white" aria-label="Mensagens">
              <MessageSquare size={19} />
              <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">5</span>
            </button>
            <div className="flex items-center gap-3 border-l border-white/10 pl-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFB800] font-display text-sm font-bold text-black">
                VR
              </span>
              <div className="hidden text-left sm:block">
                <p className="text-sm font-semibold leading-tight text-white">{user.name}</p>
                <p className="text-[11px] text-zinc-500">Administrador</p>
              </div>
              <ChevronDown size={15} className="text-zinc-500" />
              <button
                onClick={logout}
                data-testid="admin-logout-button"
                className="ml-1 text-zinc-500 transition-colors hover:text-red-400"
                aria-label="Sair"
                title="Sair"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-5 lg:p-8">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="carrossel" element={<CarouselPage />} />
            <Route path="depoimentos" element={<ReviewsPage />} />
            <Route path="integracoes" element={<IntegrationsPage />} />
            <Route path="personalizar" element={<ThemePage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="orcamentos" element={<QuotesPage />} />
            <Route path="ordens" element={<OrdersPage />} />
            <Route path="clientes" element={<ClientsPage />} />
            <Route path="financeiro" element={<FinancialPage />} />
            <Route path="*" element={<Navigate to="/admindashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
